/** Resolve Mongo/ObjectId or populated doc to string id. */
export const resolveEntityId = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    const id = value._id ?? value.id;
    return id != null ? String(id) : "";
  }
  return String(value);
};

const normalizeEmail = (email) => String(email || "").trim().toLowerCase();

/** Logged-in salesperson employee id (leads slice, then email lookup). Matches HourlyRates.jsx settings. */
export const getLoggedSalesUserId = (loggedInUserId, employees, email) => {
  const fromSlice = resolveEntityId(loggedInUserId);
  if (fromSlice) return fromSlice;
  const normalized = normalizeEmail(email);
  const employee = (employees || []).find(
    (emp) => normalizeEmail(emp.EmpEmail) === normalized
  );
  return resolveEntityId(employee?._id);
};

/**
 * Scope rates to logged-in user (same as Settings → Hourly Rates & Expenses).
 * @param onlyWhenL3 - when true, scope only for L3 sales users (Itemized P&L main table admin bypass)
 */
export const scopeSalesHourlyRatesForUser = (
  rates,
  { loggedUserId, isL3User, onlyWhenL3 = false } = {}
) => {
  const list = Array.isArray(rates) ? rates : [];
  const normalizedLoggedUserId = resolveEntityId(loggedUserId).toLowerCase().trim();
  const shouldScope = loggedUserId && (!onlyWhenL3 || isL3User);
  if (!shouldScope) return list;
  return list.filter(
    (rate) =>
      resolveEntityId(rate.createdBy).toLowerCase().trim() === normalizedLoggedUserId
  );
};

export const scopeOtherExpensesForUser = (
  otherExpenses,
  { loggedUserId, isL3User, onlyWhenL3 = false } = {}
) => {
  const list = Array.isArray(otherExpenses) ? otherExpenses : [];
  const normalizedLoggedUserId = resolveEntityId(loggedUserId).toLowerCase().trim();
  const shouldScope = loggedUserId && (!onlyWhenL3 || isL3User);
  if (!shouldScope) return list;
  return list.filter(
    (expense) =>
      resolveEntityId(expense.createdBy).toLowerCase().trim() === normalizedLoggedUserId
  );
};

const normalizeExpenseDescription = (description) =>
  String(description || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");

const compactExpenseDescription = (description) =>
  normalizeExpenseDescription(description).replace(/\s+/g, "");

/** Perdiem / Average cost rows from scoped Other Expenses (Settings source of truth). */
export const getPerdiemAndAverageCostFromExpenses = (expensesForUser) => {
  const list = Array.isArray(expensesForUser) ? expensesForUser : [];
  const perdiemExpense = list.find((expense) => {
    const d = normalizeExpenseDescription(expense.description);
    const compact = compactExpenseDescription(expense.description);
    return d === "perdiem" || d === "per diem" || compact === "perdiem";
  });
  const averageCostExpense = list.find((expense) => {
    const d = normalizeExpenseDescription(expense.description);
    const compact = compactExpenseDescription(expense.description);
    return (
      d === "average cost" ||
      d === "avg cost" ||
      compact === "averagecost" ||
      compact === "avgcost" ||
      (d.includes("average") && d.includes("cost")) ||
      (d.includes("avg") && d.includes("cost"))
    );
  });
  return {
    perdiem: perdiemExpense ? Number(perdiemExpense.cost) || 0 : 0,
    averagecost: averageCostExpense ? Number(averageCostExpense.cost) || 0 : 0,
  };
};

/**
 * Response Received / QuotedExpenseTable: sum description line values plus
 * installation trip (perdiem + average cost) for the logged-in salesperson.
 */
export const computePersonOtherExpensesFromDescriptions = (
  descriptions,
  perdiem,
  averagecost
) => {
  const list = Array.isArray(descriptions) ? descriptions : [];
  const totalValue = list.reduce(
    (sum, desc) => sum + (Number(desc?.value) || 0),
    0
  );
  const first = list[0];
  if (
    first?.typeservice &&
    String(first.typeservice).toLowerCase().includes("installation") &&
    first.tripduration
  ) {
    const tripDuration = Number(first.tripduration) || 0;
    return (
      totalValue +
      tripDuration * (Number(perdiem) || 0) +
      tripDuration * (Number(averagecost) || 0)
    );
  }
  return totalValue;
};
